<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-800 to-purple-800 text-white">
    <StructuredData 
      type="WebPage"
      :data="{
        name: 'Payment Reminder - Customizable Payment Management App',
        description: 'Flexible and adaptable payment management that fits your unique needs. Track payments with color-coded status indicators, custom categories, and comprehensive analytics.',
        url: 'https://smartpayreminder.com/',
        mainEntity: {
          '@type': 'SoftwareApplication',
          'name': 'Payment Reminder',
          'applicationCategory': 'FinanceApplication',
          'operatingSystem': 'Web, Android, iOS',
          'offers': {
            '@type': 'Offer',
            'price': '4.99',
            'priceCurrency': 'USD',
            'availability': 'https://schema.org/InStock'
          },
          'description': 'Mobile payment management app with status tracking, custom categories, notifications, and analytics.',
          'featureList': [
            'Real-time payment status monitoring',
            'Custom payment categories',
            'Flexible payment scheduling',
            'Comprehensive analytics',
            'Smart notification system'
          ],
          'screenshot': 'https://smartpayreminder.com/images/screenshots/analytics_monthly_payment_distribution.webp',
          'aggregateRating': {
            '@type': 'AggregateRating',
            'ratingValue': '4.8',
            'reviewCount': '156'
          }
        }
      }"
    />
    <!-- Navigation -->
    <nav class="container mx-auto px-4 py-6">
      <div class="flex justify-between items-center">
        <h1 class="text-xl sm:text-2xl font-bold hover-scale">Payment Reminder</h1>
        
        <!-- Mobile menu button -->
        <button 
          @click="mobileMenuOpen = !mobileMenuOpen" 
          class="md:hidden flex items-center px-3 py-2 border rounded text-white border-white/40 hover:text-white/80"
          aria-label="Toggle mobile menu"
        >
          <svg class="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
            <path v-if="!mobileMenuOpen" fill-rule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd"></path>
            <path v-else fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
          </svg>
        </button>
        
        <!-- Desktop menu -->
        <div class="hidden md:flex gap-6 items-center">
          <a 
            href="#features" 
            class="hover:text-white/80 hover-scale transition-all"
            @click.prevent="smoothScroll('features')"
          >
            Features
          </a>
          <a 
            href="#screenshots" 
            class="hover:text-white/80 hover-scale transition-all"
            @click.prevent="smoothScroll('screenshots')"
          >
            Screenshots
          </a>
          <a 
            href="#pricing" 
            class="hover:text-white/80 hover-scale transition-all"
            @click.prevent="smoothScroll('pricing')"
          >
            Pricing
          </a>
          <button 
            @click="showCredits = true"
            class="hover:text-white/80 hover-scale transition-all"
          >
            About
          </button>
          <button
            @click="goToLogin"
            class="hover:text-white/80 hover-scale transition-all pl-4 border-l border-white/20"
          >
            Login
          </button>
        </div>
      </div>
      
      <!-- Mobile menu (dropdown) -->
      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="transform scale-95 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-100 ease-in"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-95 opacity-0"
      >
        <div v-if="mobileMenuOpen" class="md:hidden mt-4 bg-white/10 backdrop-blur-sm rounded-lg p-4 shadow-lg">
          <div class="flex flex-col gap-4">
            <a 
              href="#features" 
              class="hover:text-white/80 transition-all py-2"
              @click="navigateAndCloseMenu('features')"
            >
              Features
            </a>
            <a 
              href="#screenshots" 
              class="hover:text-white/80 transition-all py-2"
              @click="navigateAndCloseMenu('screenshots')"
            >
              Screenshots
            </a>
            <a 
              href="#pricing" 
              class="hover:text-white/80 transition-all py-2"
              @click="navigateAndCloseMenu('pricing')"
            >
              Pricing
            </a>
            <button 
              @click="showCreditsAndCloseMenu()"
              class="hover:text-white/80 transition-all text-left py-2"
            >
              About
            </button>
            <button 
              @click="goToLogin"
              class="hover:text-white/80 transition-all text-left py-2"
            >
              Admin Login
            </button>
          </div>
        </div>
      </transition>
    </nav>

    <!-- Hero -->
    <div class="container mx-auto px-4 py-8 md:py-12">
      <div class="flex flex-col items-center text-center mb-10 md:mb-20">
        <h1 class="text-3xl md:text-4xl lg:text-6xl font-bold mb-8 md:mb-12 gradient-text tracking-tight leading-relaxed px-2 py-4 hover-glow">
          <div class="flex flex-col md:flex-row md:gap-4 justify-center items-center">
            <span class="text-2xl md:text-4xl lg:text-5xl">Customizable</span>
            <span class="text-2xl md:text-4xl lg:text-5xl">Payment</span>
            <span class="text-2xl md:text-4xl lg:text-5xl">Management</span>
          </div>
        </h1>
        <p class="text-lg md:text-2xl mb-8 md:mb-12 max-w-3xl mt-4 md:mt-8 hover-glow">
          Flexible and adaptable payment management that fits your unique needs. Customize every aspect of your payment tracking experience, whether you're an individual, freelancer, or business owner.
        </p>
      </div>

      <!-- Screenshots Section (Moved here from below) -->
      <div id="screenshots" class="mb-20">
        <h2 class="text-4xl font-bold text-center mb-8 fade-in hover-glow">Interface Showcase</h2>
        <p class="text-center text-xl max-w-3xl mx-auto mb-12">
          Explore our intuitive user interface designed to guide you through payment management with ease.
          See the onboarding experience that helps users get started quickly.
        </p>
        
        <ScreenshotCarousel 
          :screenshots="keyScreenshots" 
          :autoplay="true" 
          :interval="5000"
        />
        
        <div class="text-center mt-12">
          <NuxtLink 
            to="/screenshots" 
            class="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm px-6 py-3 rounded-lg transition-all"
          >
            View Complete User Guide <i class="pi pi-arrow-right"></i>
          </NuxtLink>
        </div>
      </div>

      <!-- Features Section with Clear Title -->
      <div id="features" class="mb-20">
        <h2 class="text-4xl font-bold text-center mb-12 fade-in hover-glow">Key Features</h2>
        <div class="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
          <!-- Payment Status Monitoring -->
          <div class="card-hover animate-card bg-white/10 backdrop-blur-sm rounded-lg p-6 md:p-10 block">
            <div class="flex flex-col h-full">
              <h3 class="text-lg md:text-xl font-bold mb-2 md:mb-4">🔔 Payment Status Monitoring</h3>
              <p class="text-lg md:text-xl mb-2 md:mb-4">Track payments in real-time:</p>
              <ul class="mt-2 space-y-1 md:space-y-2 flex-grow">
                <li class="flex">
                  <span class="mr-2 flex-shrink-0">•</span>
                  <span>Color-coded status indicators</span>
                </li>
                <li class="flex">
                  <span class="mr-2 flex-shrink-0">•</span>
                  <span>Quick payment actions (mark paid, skip)</span>
                </li>
                <li class="flex">
                  <span class="mr-2 flex-shrink-0">•</span>
                  <span>Custom amount input for payments</span>
                </li>
              </ul>
            </div>
          </div>

          <!-- Payment Management -->
          <div class="card-hover animate-card bg-white/10 backdrop-blur-sm rounded-lg p-6 md:p-10 block">
            <div class="flex flex-col h-full">
              <h3 class="text-lg md:text-xl font-bold mb-2 md:mb-4">⚡ Payment Management</h3>
              <p class="text-lg md:text-xl mb-2 md:mb-4">Complete payment control:</p>
              <ul class="mt-2 space-y-1 md:space-y-2 flex-grow">
                <li class="flex">
                  <span class="mr-2 flex-shrink-0">•</span>
                  <span>Customizable frequencies (daily, weekly, monthly)</span>
                </li>
                <li class="flex">
                  <span class="mr-2 flex-shrink-0">•</span>
                  <span>Multiple payment times per frequency</span>
                </li>
                <li class="flex">
                  <span class="mr-2 flex-shrink-0">•</span>
                  <span>Easy scheduling mode</span>
                </li>
                <li class="flex">
                  <span class="mr-2 flex-shrink-0">•</span>
                  <span>Detailed payment information</span>
                </li>
              </ul>
            </div>
          </div>

          <!-- Category System -->
          <div class="card-hover animate-card bg-white/10 backdrop-blur-sm rounded-lg p-6 md:p-10 block">
            <div class="flex flex-col h-full">
              <h3 class="text-lg md:text-xl font-bold mb-2 md:mb-4">📑 Category System</h3>
              <p class="text-lg md:text-xl mb-2 md:mb-4">Organize with custom categories:</p>
              <ul class="mt-2 space-y-1 md:space-y-2 flex-grow">
                <li class="flex">
                  <span class="mr-2 flex-shrink-0">•</span>
                  <span>Create custom categories with colors</span>
                </li>
                <li class="flex">
                  <span class="mr-2 flex-shrink-0">•</span>
                  <span>Edit and manage existing categories</span>
                </li>
                <li class="flex">
                  <span class="mr-2 flex-shrink-0">•</span>
                  <span>Visual category indicators</span>
                </li>
                <li class="flex">
                  <span class="mr-2 flex-shrink-0">•</span>
                  <span>Group payments for better management</span>
                </li>
              </ul>
            </div>
          </div>

          <!-- Payment Analytics -->
          <div class="card-hover animate-card bg-white/10 backdrop-blur-sm rounded-lg p-6 md:p-10 block">
            <div class="flex flex-col h-full">
              <h3 class="text-lg md:text-xl font-bold mb-2 md:mb-4">📊 Payment Analytics</h3>
              <p class="text-lg md:text-xl mb-2 md:mb-4">Comprehensive insights:</p>
              <ul class="mt-2 space-y-1 md:space-y-2 flex-grow">
                <li class="flex">
                  <span class="mr-2 flex-shrink-0">•</span>
                  <span>Payment history visualization</span>
                </li>
                <li class="flex">
                  <span class="mr-2 flex-shrink-0">•</span>
                  <span>Monthly/yearly trend analysis</span>
                </li>
                <li class="flex">
                  <span class="mr-2 flex-shrink-0">•</span>
                  <span>Category distribution insights</span>
                </li>
                <li class="flex">
                  <span class="mr-2 flex-shrink-0">•</span>
                  <span>Payment punctuality tracking</span>
                </li>
                <li class="flex">
                  <span class="mr-2 flex-shrink-0">•</span>
                  <span>Payment variance analysis</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Pricing -->
      <div id="pricing" class="mb-20">
        <h2 class="text-4xl font-bold text-center mb-12 fade-in hover-glow">Start Free, Grow Premium</h2>
        <!-- Pricing Cards -->
        <div class="grid md:grid-cols-2 gap-12 max-w-4xl mx-auto">
          <!-- Trial Tier -->
          <div 
            class="card-hover animate-card bg-white/10 backdrop-blur-sm rounded-lg p-8 flex flex-col cursor-pointer"
            @click="router.push('/waitlist?tier=trial')"
          >
            <div class="h-8"></div>
            <h3 class="text-2xl font-bold mb-2 text-center">Trial</h3>
            <div class="text-white/70 mb-6 text-center">
              <p class="mb-1">Try all features for 60 days</p>
              <p class="text-sm">No credit card required</p>
            </div>
            <div class="text-center mb-6">
              <p class="text-3xl font-bold inline-flex items-baseline justify-center w-full">
                $0<span class="text-lg font-normal">/60 days</span>
              </p>
            </div>
            <ul class="space-y-4 mb-8 flex-grow">
              <li class="flex items-center justify-center text-center">
                <span class="mr-2">✓</span>
                <span>All core features included</span>
              </li>
              <li class="flex items-center justify-center text-center">
                <span class="mr-2">✓</span>
                <span>Up to 5 payment trackers</span>
              </li>
              <li class="flex items-center justify-center text-center">
                <span class="mr-2">✓</span>
                <span>60-day trial period</span>
              </li>
              <li class="flex items-center justify-center text-center text-white/70 text-sm">
                <span>Upgrade to Premium for unlimited payments</span>
              </li>
            </ul>
            <button class="w-full py-3 bg-white text-purple-600 rounded-lg font-bold hover:bg-white/90 transition-colors">
              Start 60-Day Trial
            </button>
          </div>

          <!-- Premium Tier -->
          <div 
            class="card-hover animate-card bg-gradient-to-br from-purple-900/40 to-blue-900/40 backdrop-blur-sm rounded-lg p-8 flex flex-col cursor-pointer"
            @click="router.push('/waitlist?tier=premium')"
          >
            <div class="inline-block px-3 py-1 bg-purple-500/30 rounded-full text-sm mb-4 self-center">RECOMMENDED</div>
            <h3 class="text-2xl font-bold mb-2 text-center">Premium</h3>
            <p class="text-white/70 mb-6 text-center">Unlimited & Forever</p>
            <div class="text-center mb-6">
              <p class="text-3xl font-bold inline-flex items-baseline justify-center w-full">
                $4.99<span class="text-lg font-normal">/month</span>
              </p>
            </div>
            <ul class="space-y-4 mb-8 flex-grow">
              <li class="flex items-center justify-center text-center">
                <span class="mr-2">✨</span>
                <span>Unlimited payment tracking</span>
              </li>
              <li class="flex items-center justify-center text-center">
                <span class="mr-2">🔔</span>
                <span>All premium features included</span>
              </li>
              <li class="flex items-center justify-center text-center">
                <span class="mr-2">⚡</span>
                <span>Priority support & updates</span>
              </li>
              <li class="flex items-center justify-center text-center">
                <span class="mr-2">🔄</span>
                <span>No time limitations</span>
              </li>
            </ul>
            <button class="w-full py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg font-bold hover:opacity-90 transition-opacity">
              Go Premium
            </button>
          </div>
        </div>
      </div>

      <!-- Credits Popup -->
      <CreditsPopup 
        :is-open="showCredits"
        @close="showCredits = false"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
const client = useSupabaseClient()
const user = useSupabaseUser()
const router = useRouter()
const mobileMenuOpen = ref(false)

const goToLogin = () => {
  mobileMenuOpen.value = false
  router.push('/login')
}

const smoothScroll = (elementId: string) => {
  const element = document.getElementById(elementId)
  element?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  })
}

const navigateAndCloseMenu = (elementId: string) => {
  mobileMenuOpen.value = false
  setTimeout(() => {
    smoothScroll(elementId)
  }, 100)
}

const showCreditsAndCloseMenu = () => {
  mobileMenuOpen.value = false
  showCredits.value = true
}

watchEffect(() => {
  if (user.value) {
    router.push('/dashboard')
  }
})

import { onMounted, ref } from 'vue';

const showCredits = ref(false)

// Key screenshots for landing page carousel
const keyScreenshots = [
  {
    image: 'status_payment_status.webp',
    title: 'Visual Payment Status',
    description: 'Monitor all your payments at a glance with intuitive color-coded status indicators. Quickly identify overdue, upcoming, and completed payments through our easy-to-understand visual interface.',
    alt: 'Color-coded payment status interface showing overdue, upcoming, and future payments with intuitive visual indicators'
  },
  {
    image: 'management_edit_payment_times_easy.webp',
    title: 'Simple Payment Scheduling',
    description: 'Create and manage multiple payment dates effortlessly with our simplified scheduling interface. Perfect for quickly setting up recurring payments without complexity.',
    alt: 'Simple payment scheduling interface with calendar view for selecting multiple payment dates'
  },
  {
    image: 'analytics_category_distribution.webp',
    title: 'Category Spending Insights',
    description: 'Visualize how your payments are distributed across different categories with interactive charts. Understand your spending patterns and identify opportunities for better financial management.',
    alt: 'Pie chart showing payment distribution across different categories with percentage breakdowns'
  },
  {
    image: 'analytics_timeliness_insights_monthly.webp',
    title: 'Monthly Payment Analysis',
    description: 'Get comprehensive insights into your monthly payment trends and behaviors. Track your payment punctuality patterns and identify opportunities to improve your financial discipline.',
    alt: 'Monthly payment analytics dashboard with charts showing payment timeliness trends and statistics'
  },
  {
    image: 'analytics_payment_variance_payments.webp',
    title: 'Payment Variance Tracking',
    description: 'Monitor differences between expected and actual payment amounts over time. Track variances to better understand your spending patterns and improve financial forecasting accuracy.',
    alt: 'Graph showing payment variance between expected and actual payment amounts with trend analysis'
  }
];

onMounted(() => {
  // Immediately show cards that are in the initial viewport
  document.querySelectorAll('.card-hover').forEach(card => {
    if (card.getBoundingClientRect().top < window.innerHeight) {
      card.classList.add('animate-in');
    }
  });

  // Set up observer for cards that come into view while scrolling
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-in');
      }
    });
  }, { threshold: 0.1 });

  // Only observe cards that aren't already visible
  document.querySelectorAll('.card-hover').forEach(card => {
    if (!card.classList.contains('animate-in')) {
      observer.observe(card);
    }
  });
});

const appState = useState('app')

onMounted(() => {
  console.log('Index page mounted, app state:', appState.value)
})

// Page metadata for improved SEO
useHead({
  title: 'Payment Reminder - Customizable Payment Management App',
  meta: [
    { name: 'description', content: 'Flexible payment tracking app with real-time status monitoring, custom categories, smart notifications, and comprehensive analytics. Try free for 60 days.' },
    { name: 'keywords', content: 'payment reminder app, bill payment reminder, payment tracking app, smart bill notification system, automated payment reminder system, never miss a payment app, custom payment categories app, payment reminder with analytics, bill management with notifications, financial reminder service' },
    
    // Open Graph / Facebook
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: 'https://smartpayreminder.com/' },
    { property: 'og:title', content: 'Payment Reminder - Customizable Payment Management App' },
    { property: 'og:description', content: 'Track payments with color-coded status indicators, custom categories, flexible scheduling options, and comprehensive analytics. Start your 60-day free trial today.' },
    { property: 'og:image', content: 'https://smartpayreminder.com/images/og-payment-reminder-app.png' },
    
    // Twitter
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:url', content: 'https://smartpayreminder.com/' },
    { name: 'twitter:title', content: 'Payment Reminder - Customizable Payment Management App' },
    { name: 'twitter:description', content: 'Track payments with color-coded status indicators, custom categories, flexible scheduling options, and comprehensive analytics. Start your 60-day free trial today.' },
    { name: 'twitter:image', content: 'https://smartpayreminder.com/images/twitter-payment-reminder-app.png' }
  ],
  // Adding canonical URL to prevent duplicate content issues
  link: [
    { rel: 'canonical', href: 'https://smartpayreminder.com/' }
  ]
})
</script>

<style>
/* Hide scrollbar */
::-webkit-scrollbar {
  display: none;
}

* {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

:root, body, html {
  width: 100%;
  position: relative;
  min-height: 100%;
  margin: 0;
  padding: 0;
  scroll-behavior: smooth;
}

body {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.min-h-screen {
  min-height: 100vh;
  position: relative;
}

/* Title gradient */
.gradient-text {
  background: linear-gradient(120deg, 
    #ffffff 0%, 
    #e2e8f0 25%, 
    #ffffff 50%, 
    #e2e8f0 75%, 
    #ffffff 100%
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Hide scrollbar for Chrome, Safari and Opera */
::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
* {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.fade-in {
  animation: fadeIn 1s ease-out;
}

.hover-scale {
  transition: transform 0.2s;
}

.hover-scale:hover {
  transform: scale(1.05);
}

.gradient-text {
  background: linear-gradient(120deg, 
    #ffffff 0%, 
    #e2e8f0 25%, 
    #ffffff 50%, 
    #e2e8f0 75%, 
    #ffffff 100%
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  transition: all 0.3s ease;
}

.gradient-text:hover {
  filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.2));
  text-shadow: 
    0 0 20px rgba(255, 255, 255, 0.2),
    0 0 35px rgba(255, 255, 255, 0.1);
}

.gradient-text::after {
  content: attr(data-text);
  position: absolute;
  left: 0;
  top: 0;
  z-index: -1;
  background: linear-gradient(120deg, 
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0.1) 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: blur(8px);
}

.gradient-text::after {
  content: '';
  position: absolute;
  inset: -10px;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.1) 0%,
    transparent 70%
  );
  opacity: 0;
  animation: pulse 3s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 0; }
  50% { opacity: 1; }
}

@keyframes shine {
  to {
    background-position: 200% center;
  }
}

@keyframes glow {
  0%, 100% { opacity: 1; transform: translateY(0); }
  50% { opacity: 0.95; transform: translateY(-2px); }
}

@keyframes fade-in {
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
}

.card-hover {
  transition: all 0.4s ease;
  transform-style: preserve-3d;
  outline: none;
}

.card-hover:hover {
  transform: scale(1.03);
  box-shadow: 
    0 0 30px rgba(255, 255, 255, 0.1),
    0 0 60px rgba(255, 255, 255, 0.05);
  outline: none;
}

.hover-glow {
  transition: text-shadow 0.3s ease;
}

.hover-glow:hover {
  text-shadow: 
    0 0 15px rgba(255, 255, 255, 0.2),
    0 0 25px rgba(255, 255, 255, 0.1);
}

html {
  scroll-behavior: smooth;
}

#features, #pricing, #screenshots {
  scroll-margin-top: 80px;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
  }
}

/* Hide scrollbar */
::-webkit-scrollbar {
  display: none;
}

.animate-card {
  opacity: 0;
  transform: translateY(100px);
  transition: all 1.2s cubic-bezier(0.4, 0, 0.2, 1);  /* Changed from 0.6s to 1.2s */
}

/* Initial animation for cards in viewport */
.animate-card.animate-in {
  opacity: 1;
  transform: translateY(0);
  transition-delay: 0s; /* No delay for initial cards */
}

/* Scroll-based animation for cards not in initial viewport */
.animate-card:not(.animate-in) {
  transition-delay: 1s;  /* Changed from 0.5s to 1s */
}

/* Remove stagger effect since we want consistent timing */
/* Delete or comment out these lines:
.card-hover:nth-child(1) { transition-delay: 1s; }
.card-hover:nth-child(2) { transition-delay: 0.4s; }
.card-hover:nth-child(3) { transition-delay: 0.6s; }
*/

@keyframes floatAnimation {
  0% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0); }
}

.card-hover:hover {
  animation: floatAnimation 2s ease-in-out infinite;
}

/* Add media query for mobile */
@media (max-width: 768px) {
  .card-hover {
    transform: none !important; /* Disable transform on mobile */
    box-shadow: 
      0 0 15px rgba(255, 255, 255, 0.1),
      0 0 30px rgba(255, 255, 255, 0.05);
  }
  
  .card-hover:hover {
    transform: none !important;
  }

  /* Add these new rules for mobile */
  .animate-card {
    opacity: 1 !important;
    transform: none !important;
    transition: none !important;
    transition-delay: 0s !important;
  }

  .animate-card.animate-in {
    transition: none !important;
  }
}

/* Update fade-in animation */
@keyframes fade-in {
  0% { 
    opacity: 0; 
    transform: translateY(20px);
    -webkit-transform: translateY(20px);
  }
  100% { 
    opacity: 1; 
    transform: translateY(0);
    -webkit-transform: translateY(0);
  }
}

/* Add screenshots section style */
#screenshots, #pricing {
  scroll-margin-top: 80px;
}

/* Responsive adjustments for mobile */
@media (max-width: 768px) {
  .container {
    padding-left: 1rem;
    padding-right: 1rem;
  }
  
  #features, #pricing, #screenshots {
    scroll-margin-top: 100px;
  }
  
  .gradient-text {
    padding: 0.5rem 0;
  }
  
  /* Make grid items stack nicely on mobile */
  .grid {
    gap: 1.5rem;
  }
  
  /* Adjust card padding on mobile */
  .card-hover {
    padding: 1.25rem;
  }
}

/* Add z-index to mobile menu to ensure it appears above other content */
.md\\:hidden {
  z-index: 50;
}

/* Ensure mobile navigation transitions smoothly */
@media (max-width: 768px) {
  nav {
    position: relative;
    z-index: 100;
  }
}
</style>